import { useCallback, useEffect, useState } from 'react';
import { PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../../core/providers/AuthProvider';
import { confirmAction, showAlert, showSuccessToast } from '../../../shared/utils/dialogs';
import { clearDraft, draftKey, formatDraftTime, purgeExpiredDrafts, readDraft } from '../../../shared/utils/drafts';
import { deleteAssessment, getAssessments, saveAssessment } from '../services/gradesService';
import { TERMS } from '../../../shared/constant/terms';
import AssessmentFormModal from './AssessmentFormModal';

function AssessmentPanel({ subject, config, sections, roster }) {
  const { user } = useAuth();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [term, setTerm] = useState('');
  const [section, setSection] = useState('');
  const [editing, setEditing] = useState(null);
  const [formSection, setFormSection] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);

  const hasSections = sections.length > 0;

  // Where the form backs up unsaved entries: one draft per assessment, plus one for a new one.
  const formDraftKey = (assessmentId) =>
    draftKey(user?.id, 'assessment', subject.id, config.component, assessmentId ?? 'new');

  const loadAssessments = useCallback(async () => {
    // A subject with no sections has no students, so there is nothing to list (or to add).
    if (!hasSections) {
      setAssessments([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      setAssessments(await getAssessments(subject.id, config.component));
    } catch (err) {
      showAlert({ text: err.message || `Failed to load ${config.itemLabel} records` });
    } finally {
      setLoading(false);
    }
  }, [subject.id, config.component, config.itemLabel, hasSections]);

  useEffect(() => {
    loadAssessments();
  }, [loadAssessments]);

  useEffect(() => {
    purgeExpiredDrafts();
  }, []);

  // Unsaved entries left in the browser (e.g. by a power cut), so they can be picked up again.
  const newDraft = isFormOpen ? null : readDraft(formDraftKey(null));
  const hasDraft = (assessment) => !isFormOpen && Boolean(readDraft(formDraftKey(assessment.id)));

  const visible = assessments.filter(
    (a) => (!term || String(a.term) === term) && (!section || a.section === section)
  );

  // Each assessment counts its own section's students (an older one without a section counts everyone).
  const studentCount = (assessment) =>
    assessment.section ? roster.filter((s) => s.section === assessment.section).length : roster.length;
  const scoredCount = (assessment) => {
    const ids = new Set(
      roster.filter((s) => !assessment.section || s.section === assessment.section).map((s) => s.id)
    );
    return assessment.assessment_scores.filter((sc) => ids.has(sc.student_id)).length;
  };

  const openAddForm = () => {
    setEditing(null);
    setFormSection(section);
    setIsFormOpen(true);
  };

  const openEditForm = (assessment) => {
    setEditing(assessment);
    setIsFormOpen(true);
  };

  const handleFormSubmit = async (values) => {
    await saveAssessment({
      id: editing?.id,
      subjectId: subject.id,
      component: config.component,
      ...values,
    });
    showSuccessToast(`${config.itemLabel} ${editing ? 'updated' : 'added'}`);
    setIsFormOpen(false);
    setEditing(null);
    loadAssessments();
  };

  const handleDelete = async (assessment) => {
    const confirmed = await confirmAction({
      title: `Remove ${config.itemLabel}?`,
      text: `${assessment.title}${assessment.section ? ` (${assessment.section})` : ''} and its scores will be removed.`,
      confirmText: 'Remove',
      danger: true,
    });
    if (!confirmed) return;

    try {
      await deleteAssessment(assessment.id, assessment.title);
      clearDraft(formDraftKey(assessment.id));
      showSuccessToast(`${config.itemLabel} removed`);
      loadAssessments();
    } catch (err) {
      showAlert({ text: err.message || `Failed to remove ${config.itemLabel}` });
    }
  };

  if (!hasSections) {
    return (
      <div className="flex-1 min-h-0 bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col">
        <p className="text-center text-gray-500 py-12">
          No sections are assigned to this subject yet. Ask an admin to assign them in Subjects.
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 flex flex-col space-y-3">
      <div className="shrink-0 flex gap-3 items-center justify-between">
        {/* On phones the two filters share all the space left of the Add button equally. */}
        <div className="flex min-w-0 flex-1 gap-2 sm:flex-none">
          <select
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            aria-label="Filter by term"
            className="min-w-0 flex-1 sm:flex-none rounded-md border border-[#4ea6d0] px-3 py-2 lg:text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
          >
            <option value="">All Terms</option>
            {TERMS.map((t) => (
              <option key={t} value={t}>Term {t}</option>
            ))}
          </select>

          {sections.length > 0 && (
            <select
              value={section}
              onChange={(e) => setSection(e.target.value)}
              aria-label="Filter by section"
              className="min-w-0 flex-1 sm:flex-none rounded-md border border-[#4ea6d0] px-3 py-2 lg:text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ea6d0]"
            >
              <option value="">All Sections</option>
              {sections.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          )}
        </div>

        <button
          type="button"
          onClick={openAddForm}
          aria-label={config.addLabel}
          title={config.addLabel}
          className="shrink-0 flex items-center justify-center gap-2 rounded-md border border-[#4ea6d0] bg-[#0b4a71] px-3 sm:px-4 py-2 lg:text-sm font-medium text-white hover:bg-[#3f6d8e]"
        >
          <PlusIcon className="h-4 w-4" />
          {/* Phones show a short "Add"; the full label comes back from the sm breakpoint. */}
          <span className="sm:hidden">Add</span>
          <span className="hidden sm:inline">{config.addLabel}</span>
        </button>
      </div>

      {newDraft && (
        <div className="shrink-0 flex flex-wrap items-center justify-between gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          <span>
            You have an unsaved new {config.itemLabel} from {formatDraftTime(newDraft.savedAt)}.
          </span>
          <button type="button" onClick={openAddForm} className="font-medium underline hover:text-amber-900">
            Resume
          </button>
        </div>
      )}

      <div className="flex-1 min-h-0 bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col">
        {loading ? (
          <p className="text-center text-gray-500 py-12">Loading…</p>
        ) : visible.length === 0 ? (
          <p className="text-center text-gray-500 py-12">No {config.itemLabel} records yet.</p>
        ) : (
          <div className="flex-1 min-h-0 overflow-x-auto overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#4ea6d0] text-left text-white sticky top-0 z-10">
                <tr>
                  <th className="sticky left-0 z-20 bg-[#4ea6d0] px-4 py-3 font-medium">Title</th>
                  <th className="px-4 py-3 font-medium">Term</th>
                  <th className="px-4 py-3 font-medium">Section</th>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Max Score</th>
                  <th className="px-4 py-3 font-medium">Scored</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {visible.map((assessment) => (
                  <tr key={assessment.id} className="group hover:bg-[#4ea6d0]/20">
                    <td className="sticky left-0 z-[1] max-w-[9rem] sm:max-w-none bg-white group-hover:bg-[#dcedf6] shadow-[inset_-1px_0_0_#e5e7eb] px-4 py-3 font-medium text-[#0b4a71]">
                      {assessment.title}
                      {hasDraft(assessment) && (
                        <button
                          type="button"
                          onClick={() => openEditForm(assessment)}
                          className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 hover:bg-amber-200"
                        >
                          Unsaved changes
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">Term {assessment.term}</td>
                    <td className="px-4 py-3 text-gray-600">{assessment.section || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{assessment.assessment_date || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{Number(assessment.max_score)}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {scoredCount(assessment)} / {studentCount(assessment)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => openEditForm(assessment)}
                          aria-label={`Edit ${assessment.title}`}
                          className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 hover:text-[#0b4a71]"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(assessment)}
                          aria-label={`Remove ${assessment.title}`}
                          className="p-1.5 rounded-md text-gray-500 hover:bg-red-50 hover:text-red-600"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isFormOpen && (
        <AssessmentFormModal
          config={config}
          assessment={editing}
          defaultTerm={term || 1}
          sections={sections}
          defaultSection={formSection}
          roster={roster}
          draftStorageKey={formDraftKey(editing?.id)}
          onClose={() => setIsFormOpen(false)}
          onSubmit={handleFormSubmit}
        />
      )}
    </div>
  );
}

export default AssessmentPanel;
